import { AbstractDate } from "@linkfluence/models/date";
import { Metric } from "@linkfluence/models/metric";
import { Clause } from "@radarly/models/clause";
import {
  Dataviz,
  DatavizName,
  getDefaultDataviz,
} from "@radarly/models/dataviz";
import { SearchFragmentDateMetrics } from "@radarly/models/search";
import { isDateMetricsCardDataviz } from "../dataviz";
import { Card, DateMetricsCard } from "../model";
import { CardFactory, CardFragmentsFactory } from "./base";
import { ICardFactory } from "./model";
import { TimeUnit } from "./time-unit";

type Metrics = [Metric, Metric] | [Metric, Metric, Metric];

interface DateMetricsCardFactoryOptions {
  from?: AbstractDate;
  metrics?: Metrics;
  to?: AbstractDate;
  clause?: Clause;
  timeUnit?: TimeUnit;
}

export class DateMetricsCardFragmentsFactory extends CardFragmentsFactory {
  static create(input?: DateMetricsCardFactoryOptions) {
    const factory = new DateMetricsCardFragmentsFactory();
    return factory.getFragments(input);
  }
  static createFromCard(card: Card, input?: DateMetricsCardFactoryOptions) {
    const factory = new DateMetricsCardFragmentsFactory();
    const { from, to } = factory.getPeriod(card);
    const timeUnit = factory.getTimeUnitFromInput(input);
    return factory.getFragments({
      clause: factory.getClauseFromCard(card),
      from,
      to,
      metrics: factory.getMetricsFromCard(card, input) as Metrics,
      timeUnit,
    });
  }
  private getFragments(
    input?: DateMetricsCardFactoryOptions
  ): [SearchFragmentDateMetrics<Metrics>] {
    return [this.getFragment(input)];
  }
  private getFragment(
    input?: DateMetricsCardFactoryOptions
  ): SearchFragmentDateMetrics<Metrics> {
    const metrics = input?.metrics || [];
    const timeUnit = input?.timeUnit || "default";
    return {
      request: {
        query: this.getQuery(input),
        aggs: {
          metrics: {
            metrics: [metrics[0] || Metric.Doc, metrics[1] || Metric.Reach],
          },
          date: {
            metrics: [metrics[0] || Metric.Doc, metrics[1] || Metric.Reach],
            interval:
              timeUnit === "default" ? this.getInterval(input) : timeUnit,
          },
        },
      },
    };
  }
}
export class DateMetricsCardFactory
  implements ICardFactory<DateMetricsCard, DateMetricsCardFactoryOptions>
{
  public static getInstance() {
    return new DateMetricsCardFactory();
  }
  public fromCard(
    workspaceId: number,
    projectFocuses: number[],
    card: Card,
    input?: DateMetricsCardFactoryOptions & {
      dataviz?: Dataviz;
    }
  ) {
    return CardFactory.getInstance<DateMetricsCard>().createCard(
      workspaceId,
      projectFocuses,
      "date-metrics",
      card.title,
      DateMetricsCardFragmentsFactory.createFromCard(card, input),
      this.createDataviz(input?.dataviz || card.dataviz)
    );
  }
  public fromInput(
    workspaceId: number,
    projectFocuses: number[],
    title: string,
    input?: DateMetricsCardFactoryOptions & {
      dataviz?: Dataviz["name"];
      fullDataviz?: Dataviz;
    }
  ) {
    return CardFactory.getInstance<DateMetricsCard>().createCard(
      workspaceId,
      projectFocuses,
      "date-metrics",
      title,
      DateMetricsCardFragmentsFactory.create(input),
      this.createDataviz(
        input?.fullDataviz ??
          getDefaultDataviz(input?.dataviz || DatavizName.DoubleTimeChart)
      )
    );
  }
  public createDataviz(dataviz: Dataviz) {
    if (isDateMetricsCardDataviz(dataviz)) {
      return dataviz;
    } else {
      return getDefaultDataviz(
        DatavizName.LineChart
      ) as DateMetricsCard["dataviz"];
    }
  }
}
